import React from 'react';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CheckCircle, Edit2, Trash2, Clock } from 'lucide-react';

const rowClassFor = (entry) => {
  if (entry.status === 'open') return 'border-blue-200 bg-blue-50/30';
  if (entry.outcome === 'win') return 'border-green-200 bg-green-50/30';
  if (entry.outcome === 'loss') return 'border-red-200 bg-red-50/30';
  return 'border-gray-200 bg-white';
};

const avatarClassFor = (entry) => {
  if (entry.status === 'open') return 'bg-blue-500';
  if (entry.outcome === 'win') return 'bg-green-500';
  if (entry.outcome === 'loss') return 'bg-red-500';
  return 'bg-gray-500';
};

const TradeRow = ({ entry, onEdit, onClose, onDelete }) => (
  <div className={`p-4 border rounded-lg ${rowClassFor(entry)}`} data-testid={`trade-row-${entry.id}`}>
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${avatarClassFor(entry)}`}>
          {entry.symbol?.charAt(0)}
        </div>
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-lg text-gray-900">{entry.symbol}</span>
            <Badge variant="outline" className={`text-xs ${entry.side === 'buy' || entry.side === 'long' ? 'border-green-500 text-green-600' : 'border-red-500 text-red-600'}`}>
              {entry.side?.toUpperCase()}
            </Badge>
            <Badge variant="outline" className="text-xs">{entry.tradeType || 'stock'}</Badge>
            {entry.status === 'open' && <Badge className="bg-blue-500 text-white text-xs">OPEN</Badge>}
            {entry.outcome === 'win' && <Badge className="bg-green-500 text-white text-xs">WIN</Badge>}
            {entry.outcome === 'loss' && <Badge className="bg-red-500 text-white text-xs">LOSS</Badge>}
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
            {entry.entryPrice && <span>Entry: ${entry.entryPrice}</span>}
            {entry.exitPrice && <span>Exit: ${entry.exitPrice}</span>}
            {entry.size && <span>Size: {entry.size}</span>}
            {entry.strategy && <span className="text-purple-600">{entry.strategy}</span>}
          </div>
        </div>
      </div>

      <div className="text-center">
        {entry.pnl !== undefined && entry.pnl !== null ? (
          <div>
            <p className={`text-2xl font-bold ${entry.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {entry.pnl >= 0 ? '+' : ''}{entry.pnl?.toLocaleString()}
            </p>
            <p className={`text-sm ${entry.pnlPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {entry.pnlPercent >= 0 ? '+' : ''}{entry.pnlPercent}%
            </p>
          </div>
        ) : (
          <p className="text-gray-400">--</p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {entry.status === 'open' && (
          <Button size="sm" onClick={() => onClose(entry)} className="bg-green-600 hover:bg-green-700" data-testid={`trade-close-${entry.id}`}>
            <CheckCircle size={14} className="mr-1" /> Close
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => onEdit(entry)} data-testid={`trade-edit-${entry.id}`}>
          <Edit2 size={14} />
        </Button>
        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(entry.id)} data-testid={`trade-delete-${entry.id}`}>
          <Trash2 size={14} />
        </Button>
      </div>
    </div>

    {(entry.thesis || entry.notes || entry.mistakes || entry.lessons) && (
      <div className="mt-4 pt-4 border-t border-gray-200 grid md:grid-cols-2 gap-4 text-sm">
        {entry.thesis && (
          <div>
            <p className="font-semibold text-gray-700">Thesis:</p>
            <p className="text-gray-600">{entry.thesis}</p>
          </div>
        )}
        {entry.notes && (
          <div>
            <p className="font-semibold text-gray-700">Notes:</p>
            <p className="text-gray-600">{entry.notes}</p>
          </div>
        )}
        {entry.mistakes && (
          <div>
            <p className="font-semibold text-red-700">Mistakes:</p>
            <p className="text-gray-600">{entry.mistakes}</p>
          </div>
        )}
        {entry.lessons && (
          <div>
            <p className="font-semibold text-green-700">Lessons:</p>
            <p className="text-gray-600">{entry.lessons}</p>
          </div>
        )}
      </div>
    )}

    <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-gray-500">
      {entry.duration && <span className="flex items-center gap-1"><Clock size={12} /> {entry.duration}</span>}
      {entry.emotions && <Badge variant="outline" className="text-xs">{entry.emotions}</Badge>}
      {entry.tags?.map((tag) => <Badge key={tag} variant="secondary" className="text-xs">#{tag}</Badge>)}
      {entry.setup && <Badge variant="outline" className="text-xs">{entry.setup}</Badge>}
    </div>
  </div>
);

const TradesList = ({ entries, activeTab, onTabChange, loading, onEdit, onClose, onDelete }) => {
  const open = entries.filter((e) => e.status === 'open');
  const closed = entries.filter((e) => e.status === 'closed');
  const visible = activeTab === 'open' ? open : activeTab === 'closed' ? closed : entries;

  return (
    <Card data-testid="trades-list">
      <CardHeader>
        <Tabs value={activeTab} onValueChange={onTabChange}>
          <TabsList>
            <TabsTrigger value="open">Open ({open.length})</TabsTrigger>
            <TabsTrigger value="closed">Closed ({closed.length})</TabsTrigger>
            <TabsTrigger value="all">All ({entries.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Loading entries...</p>
        ) : (
          <div className="space-y-3">
            {visible.map((entry) => (
              <TradeRow
                key={entry.id}
                entry={entry}
                onEdit={onEdit}
                onClose={onClose}
                onDelete={onDelete}
              />
            ))}
            {visible.length === 0 && (
              <p className="text-center text-gray-500 py-8">No trades yet. Start by logging your first trade above.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TradesList;
